// Login.jsx — uses Auth.login() (no inline login fetch)
import React, { useEffect, useState } from "react";
import styles from "./styles.module.css";
import { AUTH_BASE } from "./data";
import { getSessionUser, login } from "./Auth";
import eye from "./Assets/eye.svg";

/* ---------------- strength helper ---------------- */
function strengthHint(pw) {
  if (!pw) return "";
  if (pw.length < 12) return "Must be at least 12 characters";
  if (pw.length >= 20) return "Great length";
  const hasU = /\p{Lu}/u.test(pw);
  const hasL = /\p{Ll}/u.test(pw);
  const hasD = /\d/.test(pw);
  const hasS = /[^\p{L}\p{N}\s]/u.test(pw);
  const classes = [hasU, hasL, hasD, hasS].filter(Boolean).length;
  return classes >= 3 ? "Looks good" : "Use 3 of: upper, lower, number, symbol";
}

/* ---------------- tiny helper for PUT JSON ---------------- */
async function putJson(url, body) {
  const res = await fetch(url, {
    method: "PUT",
    credentials: "include",
    mode: "cors",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json?.success === false) {
    const err = new Error(json?.error || `HTTP ${res.status}`);
    err.payload = json;
    throw err;
  }
  return json;
}

/* ---------------- Force-change dialog ---------------- */
export function ForcePasswordChangeDialog({ email, onDone }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [msg, setMsg] = useState("You must set a new password to continue.");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const hint = strengthHint(next);
  const canSubmit = !busy && current.trim() && next.length >= 12 && next === confirm;

  async function submit() {
    if (!canSubmit) return;
    setBusy(true);
    setMsg("Updating…");
    try {
      await putJson(`${AUTH_BASE}/auth/password`, {
        current_password: current,
        new_password: next,
      });
      setMsg("Password updated.");
      setTimeout(() => onDone?.(), 250);
    } catch (e) {
      setMsg(e?.payload?.detail || e?.message || "Update failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div role="presentation" style={{ position: "fixed", inset: 0, background: "color-mix(in oklab, black 45%, transparent)", zIndex: 5000 }} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Change password"
        className={styles.filterPanel}
        style={{
          position: "fixed",
          top: "50%", left: "50%",
          transform: "translate(-50%,-50%)",
          width: Math.min(520, window.innerWidth - 24 * 2),
          maxWidth: "95vw",
          background: "#fff",
          zIndex: 5001,
          borderRadius: 12,
          boxShadow: "0 10px 30px rgba(0,0,0,.2)",
          padding: 16,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.filterPanelHeader} style={{ borderBottom: "1px solid #eee" }}>
          <div className={styles.filterTitle}>Change your password</div>
        </div>

        <div className={styles.filterSection} style={{ marginTop: 10 }}>
          <div className={styles.muted} style={{ minHeight: 18 }}>{msg}</div>

          <div className={styles.inputWrap}>
            <label className={styles.fieldLabel}>Current password</label>
            <input
              className={styles.textInput}
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </div>

          <div className={styles.inputWrap}>
            <label className={styles.fieldLabel}>New password</label>
            <input
              className={styles.textInput}
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
            <div className={styles.muted} style={{ marginTop: 6 }}>{hint}</div>
          </div>

          <div className={styles.inputWrap}>
            <label className={styles.fieldLabel}>Confirm new password</label>
            <input
              className={styles.textInput}
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            {confirm && confirm !== next && (
              <div style={{ marginTop: 6, color: "#b71c1c" }}>Passwords do not match</div>
            )}
          </div>

          <div className={styles.filterActions} style={{ marginTop: 12 }}>
            <div className={styles.flexSpacer} />
            <button type="button" className={styles.applyBtn} disabled={!canSubmit} onClick={submit}>
              Update password
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/* ---------------- Login page ---------------- */
export default function Login() {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mustChange, setMustChange] = useState(false);
  const [identity, setIdentity] = useState(null); // { email }

  useEffect(() => {
    const hide = () => setMustChange(true); // hide login card
    window.addEventListener("hide-login", hide);
    return () => window.removeEventListener("hide-login", hide);
  }, []);

  // If already logged in: either force-change or go home
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const me = await getSessionUser();
        if (!live) return;
        if (me && me.id) {
          if (me.must_change_password) {
            setIdentity({ email: me.email });
            setMustChange(true);
            setMsg("Password change required.");
          } else {
            window.location.href = "/";
          }
        }
      } catch { /* stay on page */ }
    })();
    return () => { live = false; };
  }, []);

  async function submit(e) {
    e?.preventDefault?.();
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      // Use Auth.login() (server sets session cookie)
      await login({ email, password: pw });

      // Immediately ask /auth/me for post-login state
      const me = await getSessionUser().catch(() => null);
      const userEmail = me?.email || email;
      setIdentity({ email: userEmail });

      if (me?.must_change_password) {
        setMustChange(true);
        setMsg("Password change required.");
        return;
      }

      // All good — go home
      window.location.href = "/";
    } catch (err) {
      setMsg(err?.payload?.error || err?.message || "Login failed");
    } finally {
      setBusy(false);
    }
  }

  const hideLogin = mustChange;

  return (
    <>
      {!hideLogin && (
        <div style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)",
          padding: 16,
        }}>
          <form
            onSubmit={submit}
            className={styles.filterPanel}
            aria-label="Sign in"
            style={{
              width: 420,
              maxWidth: "95vw",
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 10px 30px rgba(0,0,0,.12)",
              padding: 16,
              zIndex: 1,
            }}
          >
            <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <img src={eye} alt="WatchGrid" className={styles.eyeLogo} />
                <div>
                  <div className={styles.brandName}>WATCHGRID</div>
                  <div className={styles.tagline}>ANPR Vehicle Intelligence</div>
                </div>
              </div>
            </div>

            <div className={styles.filterSection} style={{ marginTop: 16 }}>
              <div className={styles.muted} style={{ minHeight: 18, marginBottom: 8 }}>
                {busy ? "Signing in…" : msg || "Use your email and password."}
              </div>

              <div className={styles.inputWrap}>
                <label className={styles.fieldLabel} htmlFor="email">Email</label>
                <input
                  id="email"
                  className={styles.textInput}
                  type="email"
                  inputMode="email"
                  autoComplete="username"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className={styles.inputWrap}>
                <label className={styles.fieldLabel} htmlFor="password">Password</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8 }}>
                  <input
                    id="password"
                    className={styles.textInput}
                    type={showPw ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="••••••••••"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className={styles.secondaryBtn}
                    onClick={() => setShowPw((v) => !v)}
                    aria-pressed={showPw ? "true" : "false"}
                    title={showPw ? "Hide password" : "Show password"}
                  >
                    {showPw ? "Hide" : "Show"}
                  </button>
                </div>
              </div>

              <div className={styles.filterActions} style={{ marginTop: 12, display: "flex", justifyContent: "center", width: "100%" }}>
                <button type="submit" className={styles.applyBtn} disabled={busy} style={{ width: "100%" }}>
                  {busy ? "Signing in…" : "Sign in"}
                </button>
              </div>

              <div className={styles.muted} style={{ marginTop: 10 }}>
                If you were given a temporary password, you’ll be asked to change it after signing in.
              </div>
            </div>
          </form>
        </div>
      )}

      {mustChange && (
        <ForcePasswordChangeDialog
          email={(identity && identity.email) || email}
          onDone={() => (window.location.href = "/")}
        />
      )}
    </>
  );
}
